import { createFileRoute } from '@tanstack/react-router'
import { LockKeyhole } from 'lucide-react'

import { logoUrl } from '@/assets/asset-urls'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { clearLoginAttempts } from '@/lib/auth/oidc-bootstrap'
import { clearTokenData } from '@/lib/token/token'
import { CONTACT } from '@/utils/constants'

/**
 * Terminal stop for a Keycloak sign-in that succeeded but resolved to no usable Firms account.
 *
 * This route deliberately goes nowhere on its own: no redirect, no retry, no silent auth, no
 * timed bounce. Once the cutover flag is on, /login is a redirect shim straight to Keycloak, so
 * sending an unresolvable user there is a guaranteed loop rather than a risk of one - Keycloak
 * still holds a live session, authenticates them instantly, and the callback fails identity
 * again. Being its own route is what makes that structurally impossible rather than dependent on
 * a flag holding.
 *
 * `/no-account` is listed in LOGGED_OUT_PATHS so the bootstrap's silent-login step skips it.
 */
export const Route = createFileRoute('/no-account')({
  component: RouteComponent,
})

// One message for every reason we refuse. Whether the identity resolved to no account or to an
// ineligible one, the user does the same thing next - ring support - and support looks them up in
// admin, which tells them which it was faster than the screen could. Saying less is also the
// non-enumerating option.
const HEADING = (
  <>
    Your account doesn&apos;t have access{' '}
    <br />
    to the VouchedFor Firm Portal
  </>
)
const BODY = "Your sign-in worked, but this account isn't set up here. If you think it should be, please contact customer support."

function RouteComponent() {
  const accountsUrl = import.meta.env.VITE_ACCOUNTS_URL

  // RP-initiated: ending the Keycloak session is the only way back to a different identity,
  // because the live session is exactly what would sign them straight back in.
  //
  // Clear locally too: the redirect cannot reach localStorage, and the /login shim reads it. The
  // same goes for the tab's sign-in guards, which would otherwise show the user "Sign-in didn't
  // complete" after a sign-out they chose.
  const signOut = (): void => {
    clearTokenData()
    clearLoginAttempts()

    window.location.href = `${import.meta.env.VITE_USER_MANAGER_URL}/auth/oidc/logout`
  }

  return (
    <div data-fixed-root-size className="flex min-h-svh w-full items-center justify-center p-6 md:p-10 bg-[#022D4F]">
      <div className="w-full max-w-md md:max-w-3xl flex flex-col items-center gap-8">
        <img src={logoUrl} alt="VouchedFor" />

        <div className="flex w-full flex-col gap-6 md:flex-row md:justify-center">
          <Card className="w-full md:max-w-md">
            <CardContent className="flex flex-col items-center gap-4 px-8 py-10 text-center">
              <LockKeyhole className="size-12 text-primary" strokeWidth={1.5} aria-hidden />

              <h1 className="text-2xl font-bold text-[#022D4F]">{HEADING}</h1>

              <p className="text-muted-foreground">{BODY}</p>

              <div className="mt-2 flex flex-col gap-2">
                <p>
                  Call us:{' '}
                  <a className="font-bold text-primary" href={`tel:${CONTACT.PHONE_NUMBER.replace(/\s/g, '')}`}>
                    {CONTACT.PHONE_NUMBER}
                  </a>
                </p>
                <p>
                  Email us:{' '}
                  <a className="font-bold text-primary" href={`mailto:${CONTACT.EMAIL}`}>
                    {CONTACT.EMAIL}
                  </a>
                </p>
              </div>

              <Button className="mt-6" variant="outline" onClick={signOut}>
                Sign out
              </Button>
            </CardContent>
          </Card>

          {accountsUrl && (
            <Card className="flex w-full flex-col justify-center md:w-72 md:shrink-0">
              <CardContent className="flex flex-col items-center gap-4 px-8 py-10 text-center">
                <h2 className="text-xl font-bold text-[#022D4F]">Are you an adviser?</h2>

                <p className="text-muted-foreground">You might want to visit the VouchedFor Adviser Portal.</p>

                {/* A plain link, not a sign-out: the Keycloak session is what signs them in to Accounts. */}
                <Button className="mt-2" asChild>
                  <a href={`${accountsUrl}/login`}>Visit the Adviser Portal</a>
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  )
}
